/**
 * Custom hook for document source connection operations
 * Handles CRUD and test/sync operations for connections
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentSourceUserConnectionRequest } from '@/api/ApiClient';
import { apiClient } from '@/config/apiConfig';
import { log } from '@/utils/errors/logger';

interface UseConnectionOperationsProps {
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showInfo: (message: string) => void;
  onDataChanged: () => Promise<void>;
}

export const useConnectionOperations = ({
  showSuccess,
  showError,
  showInfo,
  onDataChanged,
}: UseConnectionOperationsProps) => {
  const { t } = useTranslation();

  const saveConnection = useCallback(async (
    data: DocumentSourceUserConnectionRequest,
    editingConnectionId?: string
  ): Promise<void> => {
    try {
      if (editingConnectionId) {
        await apiClient.updateUserConnectionApiDocumentSourcesUserConnectionsConnectionIdPut(editingConnectionId, data);
        showSuccess(t('documentSources.messages.connectionUpdated'));
      } else {
        await apiClient.createUserConnectionApiDocumentSourcesUserConnectionsPost(data);
        showSuccess(t('documentSources.messages.connectionCreated'));
      }
      await onDataChanged();
    } catch (error: unknown) {
      log.error('Failed to save connection', { error });
      const apiError = error as { response?: { data?: { detail?: string } } };
      showError(apiError.response?.data?.detail ?? t('documentSources.errors.saveFailed'));
    }
  }, [t, showSuccess, showError, onDataChanged]);

  const deleteConnection = useCallback(async (connectionId: string): Promise<void> => {
    try {
      await apiClient.deleteUserConnectionApiDocumentSourcesUserConnectionsConnectionIdDelete(connectionId);
      showSuccess(t('documentSources.messages.connectionDeleted'));
      await onDataChanged();
    } catch (error: unknown) {
      log.error('Failed to delete connection', { error });
      showError(t('documentSources.errors.deleteFailed'));
    }
  }, [t, showSuccess, showError, onDataChanged]);

  const syncConnection = useCallback(async (connectionId: string): Promise<void> => {
    try {
      await apiClient.triggerSyncApiDocumentSourcesUserConnectionsConnectionIdSyncPost(connectionId);
      showInfo(t('documentSources.messages.syncStarted'));
    } catch (error: unknown) {
      log.error('Failed to sync connection', { error });
      showError(t('documentSources.errors.syncFailed'));
    }
  }, [t, showInfo, showError]);

  const testConnection = useCallback(async (connectionId: string): Promise<void> => {
    try {
      const response = await apiClient.testConnectionApiDocumentSourcesUserConnectionsConnectionIdTestGet(connectionId);
      const result = response.data;
      if (result.success) {
        showSuccess(t('documentSources.messages.testSuccess'));
      } else {
        showError(result.message ?? t('documentSources.errors.testFailed'));
      }
    } catch (error: unknown) {
      log.error('Failed to test connection', { error });
      showError(t('documentSources.errors.testFailed'));
    }
  }, [t, showSuccess, showError]);

  return {
    saveConnection,
    deleteConnection,
    syncConnection,
    testConnection,
  };
};